import type { ShiftAdmin } from "../../../api/types";
import { cn } from "../../../components/ui";
import { AttentionIcon, CrewBadges, SHIFT_TONE } from "./shiftUi";

const HOUR_PX = 56;
const toMin = (t: string) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };

interface Placed { s: ShiftAdmin; start: number; end: number; col: number; cols: number }

function place(shifts: ShiftAdmin[]): Placed[] {
  const items = shifts.map((s) => {
    const start = toMin(s.start_time_local as string);
    let end = toMin(s.end_time_local as string);
    if (s.ends_next_day || end <= start) end += 1440;
    return { s, start, end, col: 0, cols: 1 };
  }).sort((a, b) => a.start - b.start || b.end - a.end);
  let group: Placed[] = [];
  let groupEnd = -1;
  const flush = () => { const n = Math.max(...group.map((g) => g.col)) + 1; group.forEach((g) => { g.cols = n; }); group = []; };
  for (const it of items) {
    if (group.length && it.start >= groupEnd) flush();
    const used = new Set(group.filter((g) => g.end > it.start).map((g) => g.col));
    let c = 0;
    while (used.has(c)) c += 1;
    it.col = c;
    group.push(it);
    groupEnd = Math.max(groupEnd, it.end);
  }
  if (group.length) flush();
  return items;
}

const hourLabel = (h: number) => {
  const hh = h % 24;
  return `${hh % 12 === 0 ? 12 : hh % 12} ${hh < 12 ? "AM" : "PM"}${h >= 24 ? " +1" : ""}`;
};

function Block({ p, first, onOpen }: { p: Placed; first: number; onOpen: (id: string) => void }) {
  const { s } = p;
  const top = ((p.start - first) / 60) * HOUR_PX;
  const height = Math.max(((p.end - p.start) / 60) * HOUR_PX - 2, 30);
  return (
    <button type="button" onClick={() => onOpen(s.id)} data-testid={`calendar-day-block-${s.id}`}
      style={{ top, height, left: `calc(${(p.col / p.cols) * 100}% + 2px)`, width: `calc(${100 / p.cols}% - 4px)` }}
      className={cn("absolute flex items-start gap-1 overflow-hidden rounded-sm border px-1.5 py-1 text-left text-[11px] leading-tight transition-colors hover:brightness-125", SHIFT_TONE[s.status])}>
      <AttentionIcon show={s.needs_attention} testId={`calendar-shift-flag-${s.id}`} />
      <span className="min-w-0">
        <span className="block truncate font-semibold">{s.employee_name}</span>
        <span className="block truncate opacity-80">{s.start_label}–{s.end_label}{p.end > 1440 ? " (next day)" : ""} {s.tz_abbr}</span>
        <span className="block truncate opacity-80">{s.venue_name}{s.role ? ` · ${s.role}` : ""} · {s.status.toLowerCase()}</span>
        <CrewBadges s={s} testPrefix="calendar-day" compact />
      </span>
    </button>
  );
}

export function DayView({ shifts, onOpen }: { shifts: ShiftAdmin[]; onOpen: (id: string) => void }) {
  const timed = shifts.filter((s) => s.start_time_local && s.end_time_local);
  const untimed = shifts.filter((s) => !(s.start_time_local && s.end_time_local));
  const placed = place(timed);
  const first = Math.min(6, ...placed.map((p) => Math.floor(p.start / 60))) ;
  const last = Math.max(24, ...placed.map((p) => Math.ceil(p.end / 60)));
  const hours = Array.from({ length: last - first }, (_, i) => first + i);
  return (
    <div data-testid="calendar-day-view" className="overflow-hidden rounded-sm border border-white/10">
      {untimed.length > 0 && (
        <div data-testid="calendar-day-untimed" className="space-y-1 border-b border-white/10 bg-surface p-2">
          <p className="label-caps text-zinc-500">All day / no time set</p>
          <div className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
            {untimed.map((s) => (
              <button key={s.id} type="button" onClick={() => onOpen(s.id)} data-testid={`calendar-day-block-${s.id}`}
                className={cn("flex items-start gap-1 rounded-sm border px-1.5 py-1 text-left text-[11px] leading-tight hover:brightness-125", SHIFT_TONE[s.status])}>
                <AttentionIcon show={s.needs_attention} testId={`calendar-shift-flag-${s.id}`} />
                <span className="min-w-0"><span className="block truncate font-semibold">No times · {s.employee_name}</span>
                  <span className="block truncate opacity-80">{s.venue_name}{s.role ? ` · ${s.role}` : ""} · {s.status.toLowerCase()}</span></span>
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="relative flex bg-ink">
        <div className="w-12 shrink-0 sm:w-16" aria-hidden>
          {hours.map((h) => <div key={h} style={{ height: HOUR_PX }} className="border-t border-white/5 pr-1.5 pt-0.5 text-right font-mono text-[10px] text-zinc-500">{hourLabel(h)}</div>)}
        </div>
        <div className="relative min-w-0 flex-1" style={{ height: hours.length * HOUR_PX }}>
          {hours.map((h, i) => <div key={h} style={{ top: i * HOUR_PX }} className={cn("absolute inset-x-0 border-t", h === 24 ? "border-signal/40" : "border-white/5")} />)}
          {placed.map((p) => <Block key={p.s.id} p={p} first={first * 60} onOpen={onOpen} />)}
          {shifts.length === 0 && <p className="absolute inset-x-0 top-8 text-center text-sm text-zinc-500">No shifts this day.</p>}
        </div>
      </div>
    </div>
  );
}
